// import { useEffect, useMemo, useState } from "react";
// import {
//   getAttendance,
//   getEmployees,
//   getLiveLocations,
//   getOfficeByTenant,
// } from "../../../../services/hrms.api";
// import {
//   computeViewFromAttendance,
//   getEmployeeDisplayName,
//   haversineKm,
//   pickDate,
//   rangeLabel,
//   ymd,
// } from "../utils/attendanceUtils";

// const NEAR_KM = 0.2;
// const CLOSE_KM = 0.5;
// const MOD_KM = 1.5;

// export default function useAttendanceData({
//   statusFilter,
//   rangeFilter,
//   search,
//   dateFrom,
//   dateTo,
//   page,
//   perPage,
// }) {
//   const [rows, setRows] = useState([]);
//   const [employees, setEmployees] = useState([]);
//   const [gps, setGps] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [officeCenter, setOfficeCenter] = useState(null);

//   // ── tenant ────────────────────────────────────────────────────────────────
//   const tenantId = useMemo(() => {
//     const raw = localStorage.getItem("user");
//     return raw ? JSON.parse(raw)?.tenant : null;
//   }, []);

//   // ── office center ─────────────────────────────────────────────────────────
//   useEffect(() => {
//     if (!tenantId) return;
//     (async () => {
//       try {
//         const { data } = await getOfficeByTenant(tenantId);
//         const primary = (data?.items || [])[0];
//         if (primary && Number.isFinite(primary.lat) && Number.isFinite(primary.lng)) {
//           setOfficeCenter({ lat: primary.lat, lng: primary.lng });
//         }
//       } catch (err) {
//         console.error("Failed to load office center", err);
//       }
//     })();
//   }, [tenantId]);

//   // ── main load ─────────────────────────────────────────────────────────────
//   const load = async () => {
//     setLoading(true);
//     try {
//       const [{ data: a }, { data: e }, { data: g }] = await Promise.all([
//         getAttendance({ limit: 200 }),
//         getEmployees(),
//         getLiveLocations(),
//       ]);

//       const raw = Array.isArray(a) ? a : a?.items || [];
//       setRows(raw.map(computeViewFromAttendance));
//       setEmployees(Array.isArray(e) ? e : e?.items || []);

//       const gArr = Array.isArray(g) ? g : g?.items || [];
//       setGps(
//         gArr.map((p) => {
//           const [lng, lat] = Array.isArray(p.location?.coordinates)
//             ? p.location.coordinates
//             : [null, null];
//           return { ...p, employeeId: String(p.employee || p.employeeId || ""), lat, lng };
//         })
//       );
//     } catch (err) {
//       console.error("[useAttendanceData] load error:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

//   // ── lookup maps ───────────────────────────────────────────────────────────
//   const gpsByEmp = useMemo(() => {
//     const map = new Map();
//     gps.forEach((p) => {
//       const key = String(p?.employeeId || p?.employee || "");
//       if (key) map.set(key, p);
//     });
//     return map;
//   }, [gps]);

//   const empById = useMemo(() => {
//     const m = new Map();
//     employees.forEach((e) => {
//       const key = String(e?.id || e?._id || e?.code || "");
//       if (key) m.set(key, e);
//     });
//     return m;
//   }, [employees]);

//   // ── enriched + filtered rows ──────────────────────────────────────────────
//   const enriched = useMemo(() => {
//     return rows
//       .map((r) => {
//         const keyId = String(r.employeeId || r.employee || "");
//         const emp = empById.get(keyId);
//         const point = gpsByEmp.get(keyId);
//         const distKm = point
//           ? haversineKm({ lat: point.lat, lng: point.lng }, officeCenter)
//           : Infinity;

//         return {
//           ...r,
//           _displayName:
//             getEmployeeDisplayName(emp) ||
//             getEmployeeDisplayName(r._empPop) ||
//             keyId ||
//             "Unknown",
//           _empRole: emp?.role || emp?.designation || "",
//           _distKm: distKm,
//           _distLabel: rangeLabel(distKm),
//         };
//       })
//       .filter((r) => {
//         const okStatus =
//           statusFilter === "All" ||
//           String(r.status).toLowerCase() === statusFilter.toLowerCase();
//         const okText = r._displayName.toLowerCase().includes(search.toLowerCase());
//         const okRange =
//           rangeFilter === "All"
//             ? true
//             : rangeFilter === "No GPS"
//             ? !Number.isFinite(r._distKm)
//             : r._distLabel === rangeFilter;
//         const dayIso = ymd(pickDate(r));
//         const okFrom = !dateFrom || (dayIso && dayIso >= dateFrom);
//         const okTo = !dateTo || (dayIso && dayIso <= dateTo);
//         return okStatus && okText && okRange && okFrom && okTo;
//       });
//   }, [rows, statusFilter, search, rangeFilter, empById, gpsByEmp, officeCenter, dateFrom, dateTo]);

//   const sortedDesc = useMemo(
//     () => [...enriched].sort((a, b) => new Date(b.date) - new Date(a.date)),
//     [enriched]
//   );

//   // ── pagination ────────────────────────────────────────────────────────────
//   const totalPages = Math.max(1, Math.ceil(sortedDesc.length / perPage));
//   const start = (page - 1) * perPage;
//   const pageRows = sortedDesc.slice(start, page * perPage);

//   // ── KPI stats ─────────────────────────────────────────────────────────────
//   const todayIso = useMemo(() => ymd(new Date()), []);

//   const checkedInToday = useMemo(
//     () =>
//       new Set(
//         rows
//           .filter((r) => {
//             const day = ymd(pickDate(r));
//             return day === todayIso && (r.in || String(r.status || "").toLowerCase() === "present");
//           })
//           .map((r) => String(r.employeeId || r.employee || ""))
//       ).size,
//     [rows, todayIso]
//   );

//   const onLeaveToday = useMemo(
//     () =>
//       new Set(
//         rows
//           .filter((r) => {
//             const day = ymd(pickDate(r));
//             return day === todayIso && String(r.status || "").toLowerCase() === "leave";
//           })
//           .map((r) => String(r.employeeId || r.employee || ""))
//       ).size,
//     [rows, todayIso]
//   );

//   const presentTodayIds = useMemo(() => {
//     const set = new Set();
//     rows.forEach((r) => {
//       const day = ymd(pickDate(r));
//       if (day !== todayIso) return;
//       const st = String(r.status || "").toLowerCase();
//       if (st === "present" || r._live || !!r.in) {
//         const id = String(r.employeeId || r.employee || "");
//         if (id) set.add(id);
//       }
//     });
//     return set;
//   }, [rows, todayIso]);

//   // ── range counts ──────────────────────────────────────────────────────────
//   const rangeCounts = useMemo(() => {
//     if (!officeCenter || !Number.isFinite(officeCenter.lat) || !Number.isFinite(officeCenter.lng)) {
//       return { inOffice: 0, nearby: 0, moderate: 0, far: 0, total: 0 };
//     }
//     let inOffice = 0, nearby = 0, moderate = 0, far = 0, total = 0;
//     gps.forEach((p) => {
//       const empId = String(p.employeeId || p.employee || "");
//       if (!presentTodayIds.has(empId)) return;
//       if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
//       const km = haversineKm({ lat: p.lat, lng: p.lng }, officeCenter);
//       total++;
//       if (km <= NEAR_KM) inOffice++;
//       else if (km <= CLOSE_KM) nearby++;
//       else if (km <= MOD_KM) moderate++;
//       else far++;
//     });
//     return { inOffice, nearby, moderate, far, total };
//   }, [gps, officeCenter, presentTodayIds]);

//   return {
//     loading,
//     sortedDesc,
//     pageRows,
//     totalPages,
//     start,
//     officeCenter,
//     rangeCounts,
//     kpi: {
//       totalEmployees: employees.length,
//       checkedInToday,
//       onLeaveToday,
//       notCheckedIn: Math.max(0, employees.length - (checkedInToday + onLeaveToday)),
//     },
//     NEAR_KM,
//     CLOSE_KM,
//     MOD_KM,
//   };
// }

// src/views/hrms/attendance/hooks/useAttendanceData.js
import { useEffect, useMemo, useState } from "react";
import {
  getAttendance,
  getEmployees,
  getLiveLocations,
  getOfficeByTenant,
  getHolidays,
  getWeeklyOffs,
  getAllEmployeeWeeklyOffs,
} from "../../../../services/hrms.api";
import {
  computeViewFromAttendance,
  getEmployeeDisplayName,
  haversineKm,
  pickDate,
  rangeLabel,
  ymd,
} from "../utils/attendanceUtils";

const NEAR_KM = 0.2;
const CLOSE_KM = 0.5;
const MOD_KM = 1.5;

// Day-of-week from "YYYY-MM-DD" string (0=Sun … 6=Sat)
function dowFromYmd(workDay) {
  const [y, mo, d] = workDay.split("-").map(Number);
  return new Date(y, mo - 1, d).getDay();
}

export default function useAttendanceData({
  statusFilter,
  rangeFilter,
  search,
  dateFrom,
  dateTo,
  page,
  perPage,
}) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [gps, setGps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [officeCenter, setOfficeCenter] = useState(null);
  const [holidays, setHolidays] = useState([]);   // Set of "YYYY-MM-DD" strings
  const [globalWeeklyOff, setGlobalWeeklyOff] = useState([0, 6]);
  const [empWeeklyOffs, setEmpWeeklyOffs] = useState(new Map()); // employeeId → [days]

  const tenantId = useMemo(() => {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw)?.tenant : null;
  }, []);

  // ── Office center ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      try {
        const { data } = await getOfficeByTenant(tenantId);
        const primary = (data?.items || [])[0];
        if (primary && Number.isFinite(primary.lat) && Number.isFinite(primary.lng)) {
          setOfficeCenter({ lat: primary.lat, lng: primary.lng });
        }
      } catch (err) {
        console.error("Failed to load office center", err);
      }
    })();
  }, [tenantId]);

  // ── Main load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const year = new Date().getFullYear();

        const [
          { data: a },
          { data: e },
          { data: g },
          holidayRes,
          weeklyOffRes,
          empWeeklyOffRes,
        ] = await Promise.all([
          getAttendance({ limit: 500 }),
          getEmployees(),
          getLiveLocations(),
          getHolidays(year).catch(() => ({ data: [] })),
          getWeeklyOffs().catch(() => ({ data: { weeklyOffDays: [0, 6] } })),
          getAllEmployeeWeeklyOffs().catch(() => ({ data: [] })),
        ]);

        // Raw attendance rows
        const raw = Array.isArray(a) ? a : a?.items || [];
        setRows(raw.map(computeViewFromAttendance));

        setEmployees(Array.isArray(e) ? e : e?.items || []);

        // GPS
        const gArr = Array.isArray(g) ? g : g?.items || [];
        setGps(
          gArr.map((p) => {
            const [lng, lat] = Array.isArray(p.location?.coordinates)
              ? p.location.coordinates
              : [null, null];
            return {
              ...p,
              employeeId: String(p.employee || p.employeeId || ""),
              lat,
              lng,
            };
          })
        );

        // Holidays → Set of YYYY-MM-DD strings
        const hArr = Array.isArray(holidayRes?.data)
          ? holidayRes.data
          : holidayRes?.data?.items || [];
        const hSet = new Set(
          hArr
            .map((h) => ymd(h.date || h.startDate || h.from))
            .filter(Boolean)
        );
        setHolidays(hSet);

        // Global weekly off
        const gwo = weeklyOffRes?.data?.weeklyOffDays;
        if (Array.isArray(gwo) && gwo.length) setGlobalWeeklyOff(gwo);

        // Per-employee weekly off map
        const ewoArr = Array.isArray(empWeeklyOffRes?.data)
          ? empWeeklyOffRes.data
          : [];
        const ewoMap = new Map();
        ewoArr.forEach((item) => {
          if (item?.employeeId && Array.isArray(item.weeklyOffDays)) {
            ewoMap.set(String(item.employeeId), item.weeklyOffDays);
          }
        });
        setEmpWeeklyOffs(ewoMap);
      } catch (err) {
        console.error("[useAttendanceData] load error:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Lookup maps ───────────────────────────────────────────────────────────
  const gpsByEmp = useMemo(() => {
    const map = new Map();
    gps.forEach((p) => {
      const key = String(p?.employeeId || p?.employee || "");
      if (key) map.set(key, p);
    });
    return map;
  }, [gps]);

  const empById = useMemo(() => {
    const m = new Map();
    employees.forEach((e) => {
      const key = String(e?.id || e?._id || "");
      if (key) m.set(key, e);
    });
    return m;
  }, [employees]);

  // ── Resolve weekly off for one employee ───────────────────────────────────
  const resolveWeeklyOff = (employeeId) => {
    const perEmp = empWeeklyOffs.get(String(employeeId));
    if (Array.isArray(perEmp) && perEmp.length) return perEmp;
    return globalWeeklyOff;
  };

  // ── Enrich rows with display name, GPS, status ────────────────────────────
  const enriched = useMemo(() => {
    return rows
      .map((r) => {
        const keyId = String(r.employeeId || r.employee || "");
        const emp = empById.get(keyId);
        const point = gpsByEmp.get(keyId);
        const distKm = point
          ? haversineKm({ lat: point.lat, lng: point.lng }, officeCenter)
          : Infinity;

        const workDay = r.workDay;
        const weeklyOff = resolveWeeklyOff(keyId);
        const dow = workDay ? dowFromYmd(workDay) : -1;
        const isWeeklyOff = dow >= 0 && weeklyOff.includes(dow);
        const isHoliday = workDay ? holidays.has(workDay) : false;

        // Override status for weekly off / holiday
        let status = r.status;
        if (isHoliday) status = "Holiday";
        else if (isWeeklyOff) status = "WeeklyOff";

        return {
          ...r,
          status,
          _weeklyOff: isWeeklyOff,
          _holiday: isHoliday,
          _displayName:
            getEmployeeDisplayName(emp) ||
            getEmployeeDisplayName(r._empPop) ||
            keyId ||
            "Unknown",
          _empRole: emp?.role || emp?.designation || "",
          _distKm: distKm,
          _distLabel: rangeLabel(distKm),
        };
      })
      .filter((r) => {
        const okStatus =
          statusFilter === "All" ||
          String(r.status).toLowerCase() === statusFilter.toLowerCase();
        const okText = r._displayName.toLowerCase().includes(search.toLowerCase());
        const okRange =
          rangeFilter === "All"
            ? true
            : rangeFilter === "No GPS"
              ? !Number.isFinite(r._distKm)
              : r._distLabel === rangeFilter;
        const dayIso = ymd(pickDate(r));
        const okFrom = !dateFrom || (dayIso && dayIso >= dateFrom);
        const okTo = !dateTo || (dayIso && dayIso <= dateTo);
        return okStatus && okText && okRange && okFrom && okTo;
      });
  }, [
    rows, statusFilter, search, rangeFilter,
    empById, gpsByEmp, officeCenter,
    dateFrom, dateTo,
    holidays, globalWeeklyOff, empWeeklyOffs,
  ]);

  const sortedDesc = useMemo(
    () => [...enriched].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [enriched]
  );

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(sortedDesc.length / perPage));
  const start = (page - 1) * perPage;
  const pageRows = sortedDesc.slice(start, page * perPage);

  // ── KPI ───────────────────────────────────────────────────────────────────
  const todayIso = useMemo(() => ymd(new Date()), []);

  const kpiToday = useMemo(() => {
    const counts = {
      present: 0, halfDay: 0, late: 0,
      absent: 0, weeklyOff: 0, holiday: 0, leave: 0,
    };
    const seen = new Set();

    rows.forEach((r) => {
      const day = ymd(pickDate(r));
      if (day !== todayIso) return;
      const id = String(r.employeeId || r.employee || "");
      if (!id || seen.has(id)) return;
      seen.add(id);

      const weeklyOff = resolveWeeklyOff(id);
      const dow = day ? dowFromYmd(day) : -1;
      const isWeeklyOff = dow >= 0 && weeklyOff.includes(dow);
      const isHoliday = holidays.has(day);

      let status = r.status;
      if (isHoliday) status = "Holiday";
      else if (isWeeklyOff) status = "WeeklyOff";

      if (status === "Present") counts.present++;
      else if (status === "HalfDay") counts.halfDay++;
      else if (status === "Late") counts.late++;
      else if (status === "Absent") counts.absent++;
      else if (status === "WeeklyOff") counts.weeklyOff++;
      else if (status === "Holiday") counts.holiday++;
      else if (status === "Leave") counts.leave++;
    });

    return counts;
  }, [rows, todayIso, holidays, globalWeeklyOff, empWeeklyOffs]);

  const presentTodayIds = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const day = ymd(pickDate(r));
      if (day !== todayIso) return;
      const st = String(r.status || "").toLowerCase();
      if (st === "present" || r._live || !!r.in) {
        set.add(String(r.employeeId || r.employee || ""));
      }
    });
    return set;
  }, [rows, todayIso]);

  // ── Range counts ──────────────────────────────────────────────────────────
  const rangeCounts = useMemo(() => {
    if (!officeCenter || !Number.isFinite(officeCenter.lat)) {
      return { inOffice: 0, nearby: 0, moderate: 0, far: 0, total: 0 };
    }
    let inOffice = 0, nearby = 0, moderate = 0, far = 0, total = 0;
    gps.forEach((p) => {
      const empId = String(p.employeeId || p.employee || "");
      if (!presentTodayIds.has(empId)) return;
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
      const km = haversineKm({ lat: p.lat, lng: p.lng }, officeCenter);
      total++;
      if (km <= NEAR_KM) inOffice++;
      else if (km <= CLOSE_KM) nearby++;
      else if (km <= MOD_KM) moderate++;
      else far++;
    });
    return { inOffice, nearby, moderate, far, total };
  }, [gps, officeCenter, presentTodayIds]);

  // ── Export rows (all enriched, no pagination) ─────────────────────────────
  const exportRows = sortedDesc;

  return {
    loading,
    sortedDesc,
    pageRows,
    totalPages,
    start,
    officeCenter,
    rangeCounts,
    exportRows,
    kpi: {
      totalEmployees: employees.length,
      checkedInToday: kpiToday.present + kpiToday.halfDay + kpiToday.late,
      onLeaveToday: kpiToday.leave,
      notCheckedIn: Math.max(
        0,
        employees.length - (kpiToday.present + kpiToday.halfDay + kpiToday.late + kpiToday.leave)
      ),
      ...kpiToday,
    },
    NEAR_KM,
    CLOSE_KM,
    MOD_KM,
  };
}